import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";

const ROW_ICON_BUTTON =
  "border-border/60 bg-background text-muted-foreground hover:border-border hover:bg-muted hover:text-foreground hover:shadow-sm active:scale-[0.96] size-8 shrink-0 rounded-md border shadow-xs transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out [&_svg]:text-inherit";

export type PastReportExportFormat = "csv" | "json" | "text" | "pdf";

export interface PastReportExportMenuProps {
  exportPending: boolean;
  revealClassName: string;
  revealStyle: CSSProperties;
  onExport: (format: PastReportExportFormat) => void;
}

/** Row-level export trigger: CSV / JSON download in place; PDF opens the report for raster export. */
export function PastReportExportMenu({
  exportPending,
  revealClassName,
  revealStyle,
  onExport,
}: PastReportExportMenuProps) {
  const { t } = useTranslation();

  return (
    <span className={cn("inline-flex", revealClassName)} style={revealStyle}>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-lg"
                disabled={exportPending}
                aria-label={t("preflight.pastReports.actionDownload")}
                className={ROW_ICON_BUTTON}
                onClick={(event) => event.stopPropagation()}
              >
                <Icon name="download" size="sm" color="inherit" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            {t("preflight.pastReports.actionDownload")}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          <DropdownMenuItem
            disabled={exportPending}
            onSelect={() => onExport("csv")}
          >
            {t("preflight.export.csv")}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={exportPending}
            onSelect={() => onExport("json")}
          >
            {t("preflight.export.json")}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={exportPending}
            onSelect={() => onExport("text")}
          >
            {t("preflight.export.text")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onExport("pdf")}>
            {t("preflight.export.pdf")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
